import { EXTRA_SPEC_RATIO_ENABLED } from '@/consts/features'
import { MachineResourceCategory } from '@/consts/machine'
import { Image } from '@/models/image'
import { Machine } from '@/models/machine'
import { Snapshot } from '@/models/snapshot'

import {
  getMinimumResourceUnitCount,
  getResourceCompatibility,
  type ResourceCapacity,
  type ResourceCompatibility,
} from '../resourceCompatibility'

type ExtraDiskUnit = {
  diskGB: number
  machineClassTag?: string
}

type ExtraCpuUnit = {
  cpuCores: number
}

type ExtraRamUnit = {
  ramGB: number
  ramMB: number
}

export type ExtraResourceConfig = {
  cpuCores?: number
  ramGB?: number
  diskGB?: number
}

export type MinResourceConfig = ResourceCapacity

/**
 * Controls whether the CPU:RAM ratio rule applies. Defaults to the build-time
 * VITE_ENABLE_EXTRA_SPEC_RATIO flag; pass `ratioEnabled` explicitly to override (tests).
 */
export type ExtraSpecPolicyOptions = {
  ratioEnabled?: boolean
}

export type ExtraResourceStep = {
  step: number
  decreaseStep: number
}

export const EMPTY_EXTRA_RESOURCE_CONFIG: ExtraResourceConfig = {}
export const EXTRA_RESOURCE_STEP = 1
export const EXTRA_CPU_MIN_CORES = 2
/** Extra disk step (and minimum) under the CPU:RAM ratio rule. */
export const RATIO_EXTRA_DISK_STEP_GB = 100
/** Extra disk step (and minimum) when the ratio rule is disabled. */
export const FREE_EXTRA_DISK_STEP_GB = 50

const EXTRA_RAM_RATIO_OPTIONS = [1, 2, 4, 8, 16]
const FREE_EXTRA_RESOURCE_STEP: ExtraResourceStep = {
  step: EXTRA_RESOURCE_STEP,
  decreaseStep: EXTRA_RESOURCE_STEP,
}

export function isExtraSpecRatioEnabled(options?: ExtraSpecPolicyOptions) {
  return options?.ratioEnabled ?? EXTRA_SPEC_RATIO_ENABLED
}

/**
 * Under the ratio rule extra RAM can only exist alongside extra CPU (and vice versa).
 * Without it the two resources are configured independently.
 */
export function isExtraRamBoundToCpu(options?: ExtraSpecPolicyOptions) {
  return isExtraSpecRatioEnabled(options)
}

/**
 * Extra disk is configured in fixed steps; the step doubles as the minimum non-zero amount.
 * 100 GB under the ratio rule, 50 GB without it.
 */
export function getExtraDiskStepGB(options?: ExtraSpecPolicyOptions) {
  return isExtraSpecRatioEnabled(options) ? RATIO_EXTRA_DISK_STEP_GB : FREE_EXTRA_DISK_STEP_GB
}

export function getExtraDiskMinGB(options?: ExtraSpecPolicyOptions) {
  return getExtraDiskStepGB(options)
}

export function buildBaseResourceDesc(machine: Machine, count: number) {
  return JSON.stringify({
    agentDomains: machine.domains,
    machineClassTag: machine.unitSpec?.classTag,
    unitQty: count,
    gpuName: machine.unitSpec?.gpuName,
    cpuName: machine.unitSpec?.cpuName,
    diskGB: machine.unitSpec?.diskGB,
  })
}

function ceilToStep(value: number, step: number) {
  if (step <= 0) return value

  return Math.ceil(value / step) * step
}

function floorToStep(value: number, step: number) {
  if (step <= 0) return value

  return Math.floor(value / step) * step
}

// Powers of two starting from EXTRA_CPU_MIN_CORES (2, 4, 8, 16, ...) up to maxCpuCores.
// Unbounded by design: the ceiling is whatever the machine actually provides.
function getExtraCpuCorePowers(maxCpuCores: number) {
  const options: number[] = []

  for (let cores = EXTRA_CPU_MIN_CORES; cores <= maxCpuCores; cores *= 2) {
    options.push(cores)
  }

  return options
}

export function getAvailableExtraCpuUnits(machine?: Machine): ExtraCpuUnit[] {
  return (
    machine?.attachments
      ?.filter(
        (attachment) =>
          attachment.category === MachineResourceCategory.CPUOnly &&
          attachment.Attachment?.Cpu?.coresPerUnit,
      )
      .map((attachment) => {
        const coresPerUnit = attachment.Attachment?.Cpu?.coresPerUnit ?? 0

        return { cpuCores: coresPerUnit }
      })
      .filter((unit) => unit.cpuCores > 0)
      .sort((a, b) => a.cpuCores - b.cpuCores) ?? []
  )
}

export function getAvailableExtraRamUnits(machine?: Machine): ExtraRamUnit[] {
  return (
    machine?.attachments
      ?.filter(
        (attachment) =>
          attachment.category === MachineResourceCategory.RAMOnly &&
          attachment.Attachment?.Ram?.MBPerUnit,
      )
      .map((attachment) => {
        const ramMBPerUnit = attachment.Attachment?.Ram?.MBPerUnit ?? 0
        const ramGBPerUnit = ramMBPerUnit / 1024

        return {
          ramGB: ramGBPerUnit,
          ramMB: ramMBPerUnit,
        }
      })
      .filter((unit) => unit.ramGB > 0)
      .sort((a, b) => a.ramGB - b.ramGB) ?? []
  )
}

export function getAvailableExtraDiskUnits(machine?: Machine): ExtraDiskUnit[] {
  return (
    machine?.attachments
      ?.filter(
        (attachment) =>
          attachment.category === MachineResourceCategory.DiskOnly &&
          attachment.Attachment?.Disk?.diskGB,
      )
      .map((attachment) => {
        const diskGB = attachment.Attachment?.Disk?.diskGB ?? 0

        return {
          diskGB,
          machineClassTag: attachment.machineClassTag,
        }
      })
      .filter((unit) => unit.diskGB > 0)
      .sort((a, b) => a.diskGB - b.diskGB) ?? []
  )
}

export function getAvailableExtraCpuCores(machine?: Machine) {
  const units = getAvailableExtraCpuUnits(machine)

  return units[units.length - 1]?.cpuCores ?? 0
}

export function getAvailableExtraRamGB(machine?: Machine) {
  const units = getAvailableExtraRamUnits(machine)
  const maxRamGB = units[units.length - 1]?.ramGB ?? 0

  return maxRamGB > 0 ? Math.ceil(maxRamGB) : 0
}

export function getMinDiskGB(source?: Image | Snapshot) {
  return getMinResource(source).diskGB
}

export function getMinResource(source?: Image | Snapshot): MinResourceConfig {
  if (source instanceof Snapshot) {
    return {
      cpuCores: 0,
      ramMB: 0,
      diskGB: source.minDiskGB ?? 0,
    }
  }

  return {
    cpuCores: source?.minResource?.cpus ?? 0,
    ramMB: source?.minResource?.ramMB ?? 0,
    diskGB: source?.minResource?.diskGB ?? 0,
  }
}

export function getRequiredExtraResourceConfig(
  machine?: Machine,
  count = 1,
  source?: Image | Snapshot,
  options?: ExtraSpecPolicyOptions,
): ExtraResourceConfig {
  if (!machine || !source) return EMPTY_EXTRA_RESOURCE_CONFIG

  const minResource = getMinResource(source)
  const baseCpuCores = (machine.unitSpec?.cpuCores ?? 0) * count
  const baseRamMB = (machine.unitSpec?.ramMB ?? 0) * count
  const baseDiskGB = (machine.unitSpec?.diskGB ?? 0) * count
  const missingCpuCores =
    minResource.cpuCores > baseCpuCores ? Math.ceil(minResource.cpuCores - baseCpuCores) : 0
  const missingRamMB = minResource.ramMB > baseRamMB ? minResource.ramMB - baseRamMB : 0
  const missingRamGB = missingRamMB > 0 ? Math.ceil(missingRamMB / 1024) : 0
  const missingDiskGB = minResource.diskGB - baseDiskGB
  const requiredDiskGB = Math.ceil(missingDiskGB)
  const extraDiskGB =
    requiredDiskGB > 0 && requiredDiskGB <= getAvailableExtraStorageGB(machine, options)
      ? requiredDiskGB
      : undefined

  if (!isExtraSpecRatioEnabled(options)) {
    // No ratio rule: top up exactly what is missing, as long as the machine can provide it.
    const availableCpuCores = getAvailableExtraCpuCores(machine)
    const availableRamGB = getAvailableExtraRamGB(machine)

    return {
      cpuCores:
        missingCpuCores > 0 && missingCpuCores <= availableCpuCores ? missingCpuCores : undefined,
      ramGB: missingRamGB > 0 && missingRamGB <= availableRamGB ? missingRamGB : undefined,
      diskGB: extraDiskGB,
    }
  }

  const needsExtraCpuOrRam = missingCpuCores > 0 || missingRamGB > 0
  const cpuOptions = needsExtraCpuOrRam ? getExtraCpuCoreOptions(machine) : []
  const extraCpuCores =
    cpuOptions.find((cpuOption) => {
      if (cpuOption < missingCpuCores) return false
      if (missingRamGB <= 0) return true

      return getExtraRamGBOptionsByCpuRatio(machine, cpuOption).some(
        (ramOption) => ramOption >= missingRamGB,
      )
    }) ?? 0
  const extraRamGB =
    extraCpuCores > 0 && missingRamGB > 0
      ? (getExtraRamGBOptionsByCpuRatio(machine, extraCpuCores).find(
          (ramOption) => ramOption >= missingRamGB,
        ) ?? 0)
      : 0

  return {
    cpuCores: extraCpuCores > 0 ? extraCpuCores : undefined,
    ramGB: extraRamGB > 0 ? extraRamGB : undefined,
    diskGB: extraDiskGB,
  }
}

export function getRequiredExtraStorageGB(
  machine?: Machine,
  count = 1,
  source?: Image | Snapshot,
  options?: ExtraSpecPolicyOptions,
) {
  return getRequiredExtraResourceConfig(machine, count, source, options).diskGB ?? 0
}

const EMPTY_RESOURCE_CAPACITY: ResourceCapacity = { cpuCores: 0, ramMB: 0, diskGB: 0 }

/** The largest extra CPU / RAM / disk a machine can add on top of its base units. */
export function getMaxExtraResourceCapacity(
  machine?: Machine,
  options?: ExtraSpecPolicyOptions,
): ResourceCapacity {
  const cpuCores = getMaxExtraCpuCores(machine, options)

  return {
    cpuCores,
    ramMB: getExtraRamGBRange(machine, cpuCores, options).max * 1024,
    diskGB: getAvailableExtraStorageGB(machine, options),
  }
}

export function getBootSourceCompatibility({
  machine,
  count = 1,
  source,
  supportsExtraResources,
  policy,
}: {
  machine?: Machine
  count?: number
  source?: Image | Snapshot
  supportsExtraResources: boolean
  policy?: ExtraSpecPolicyOptions
}): ResourceCompatibility {
  const unitCount = Number.isFinite(count) && count > 0 ? count : 0
  const baseCapacity: ResourceCapacity = {
    cpuCores: (machine?.unitSpec?.cpuCores ?? 0) * unitCount,
    ramMB: (machine?.unitSpec?.ramMB ?? 0) * unitCount,
    diskGB: (machine?.unitSpec?.diskGB ?? 0) * unitCount,
  }
  const extraCapacity = supportsExtraResources
    ? getMaxExtraResourceCapacity(machine, policy)
    : EMPTY_RESOURCE_CAPACITY

  return getResourceCompatibility({
    required: getMinResource(source),
    baseCapacity,
    extraCapacity,
    supportsExtraResources,
  })
}

export function getMinimumInstanceCountForBootSource({
  machine,
  count = 1,
  source,
  supportsExtraResources,
  policy,
}: {
  machine?: Machine
  count?: number
  source?: Image | Snapshot
  supportsExtraResources: boolean
  policy?: ExtraSpecPolicyOptions
}): number | undefined {
  if (!machine || !source) return undefined

  return getMinimumResourceUnitCount({
    required: getMinResource(source),
    unitCapacity: {
      cpuCores: machine.unitSpec?.cpuCores ?? 0,
      ramMB: machine.unitSpec?.ramMB ?? 0,
      diskGB: machine.unitSpec?.diskGB ?? 0,
    },
    extraCapacity: supportsExtraResources
      ? getMaxExtraResourceCapacity(machine, policy)
      : EMPTY_RESOURCE_CAPACITY,
    supportsExtraResources,
    currentCount: count,
    maxCount: machine.maxAvailUnits,
    step: machine.unitStep,
  })
}

export function getResourceRequirementStatus({
  machine,
  count = 1,
  source,
  extraResourceConfig,
  policy,
}: {
  machine?: Machine
  count?: number
  source?: Image | Snapshot
  extraResourceConfig?: ExtraResourceConfig
  policy?: ExtraSpecPolicyOptions
}) {
  const minResource = getMinResource(source)
  const normalizedExtraConfig = normalizeExtraResourceConfig(machine, extraResourceConfig, policy)
  const currentCpuCores =
    (machine?.unitSpec?.cpuCores ?? 0) * count + (normalizedExtraConfig.cpuCores ?? 0)
  const currentRamMB =
    (machine?.unitSpec?.ramMB ?? 0) * count + (normalizedExtraConfig.ramGB ?? 0) * 1024
  const currentDiskGB =
    (machine?.unitSpec?.diskGB ?? 0) * count + (normalizedExtraConfig.diskGB ?? 0)
  return getResourceCompatibility({
    required: minResource,
    baseCapacity: {
      cpuCores: currentCpuCores,
      ramMB: currentRamMB,
      diskGB: currentDiskGB,
    },
    supportsExtraResources: false,
  })
}

export function getAvailableExtraStorageGB(machine?: Machine, options?: ExtraSpecPolicyOptions) {
  const units = getAvailableExtraDiskUnits(machine)
  const maxDiskGB = units[units.length - 1]?.diskGB ?? 0

  return maxDiskGB > 0 ? floorToStep(Math.floor(maxDiskGB), getExtraDiskStepGB(options)) : 0
}

export function getExtraCpuStepCores(machine?: Machine) {
  return machine ? EXTRA_RESOURCE_STEP : EXTRA_RESOURCE_STEP
}

export function getExtraRamStepGB(machine?: Machine) {
  return machine ? EXTRA_RESOURCE_STEP : EXTRA_RESOURCE_STEP
}

export function getExtraStorageStepGB(_machine?: Machine, options?: ExtraSpecPolicyOptions) {
  return getExtraDiskStepGB(options)
}

export function getMinExtraStorageGB(
  machine?: Machine,
  requiredDiskGB = 0,
  options?: ExtraSpecPolicyOptions,
) {
  const maxDiskGB = getAvailableExtraStorageGB(machine, options)
  // No snapshot requirement: extra disk starts at 0 and steps up by the policy's disk step.
  if (!machine || maxDiskGB <= 0 || requiredDiskGB <= 0) return 0

  const minDiskGB = ceilToStep(requiredDiskGB, getExtraDiskStepGB(options))

  return Math.min(minDiskGB, maxDiskGB)
}

export function getExtraDiskMachineClassTag(machine?: Machine, diskGB = 0) {
  if (diskGB <= 0) return undefined

  const units = getAvailableExtraDiskUnits(machine)
  let selectedDiskGB = 0

  for (const unit of units) {
    selectedDiskGB += unit.diskGB
    if (selectedDiskGB >= diskGB) {
      return unit.machineClassTag
    }
  }

  return undefined
}

function clampToOptions(value: number, options: number[]) {
  if (value <= 0 || options.length === 0) return 0

  return options.find((option) => option >= value) ?? options[options.length - 1]
}

export function getExtraCpuCoreOptions(machine?: Machine) {
  const maxCpuCores = getAvailableExtraCpuCores(machine)
  const availableRamGB = getAvailableExtraRamGB(machine)

  return getExtraCpuCorePowers(Math.min(maxCpuCores, availableRamGB))
}

function getExtraRamRatioOptionsByCpuCores(extraCpuCores: number) {
  const maxRatio = extraCpuCores <= 2 ? 2 : Math.min(extraCpuCores, 16)

  return EXTRA_RAM_RATIO_OPTIONS.filter((ratio) => ratio <= maxRatio)
}

export function getExtraRamGBOptionsByCpuRatio(machine?: Machine, extraCpuCores = 0) {
  const availableRamGB = getAvailableExtraRamGB(machine)
  if (!machine || extraCpuCores <= 0 || availableRamGB <= 0) return []

  return getExtraRamRatioOptionsByCpuCores(extraCpuCores)
    .map((ratio) => extraCpuCores * ratio)
    .filter((ramGB) => ramGB <= availableRamGB)
}

export function getExtraRamGBRangeByCpuRatio(machine?: Machine, extraCpuCores = 0) {
  const options = getExtraRamGBOptionsByCpuRatio(machine, extraCpuCores)

  return {
    min: options[0] ?? 0,
    max: options[options.length - 1] ?? 0,
  }
}

export function getMaxExtraCpuCoresByRamRatio(machine?: Machine) {
  const options = getExtraCpuCoreOptions(machine)

  return options[options.length - 1] ?? 0
}

function clampIntegerValue(value: number, max: number) {
  if (!Number.isFinite(value) || value <= 0 || max <= 0) return 0

  return Math.min(Math.floor(value), max)
}

// ---------------------------------------------------------------------------
// Policy-neutral entry points. UI and compatibility checks should use these so
// the ratio rule can be switched on/off in one place.
// ---------------------------------------------------------------------------

export function getMaxExtraCpuCores(machine?: Machine, options?: ExtraSpecPolicyOptions) {
  if (isExtraSpecRatioEnabled(options)) return getMaxExtraCpuCoresByRamRatio(machine)

  return getAvailableExtraCpuCores(machine)
}

export function getExtraRamGBRange(
  machine?: Machine,
  extraCpuCores = 0,
  options?: ExtraSpecPolicyOptions,
) {
  if (isExtraSpecRatioEnabled(options)) {
    return getExtraRamGBRangeByCpuRatio(machine, extraCpuCores)
  }

  return { min: 0, max: getAvailableExtraRamGB(machine) }
}

// Under the ratio rule the stepper doubles / halves the current value.
export function getExtraCpuStep(cpuCores = 0, options?: ExtraSpecPolicyOptions): ExtraResourceStep {
  if (!isExtraSpecRatioEnabled(options)) return FREE_EXTRA_RESOURCE_STEP

  return {
    step: cpuCores > 0 ? cpuCores : EXTRA_CPU_MIN_CORES,
    decreaseStep: cpuCores > EXTRA_CPU_MIN_CORES ? cpuCores / 2 : EXTRA_CPU_MIN_CORES,
  }
}

export function getExtraRamStep(ramGB = 0, options?: ExtraSpecPolicyOptions): ExtraResourceStep {
  if (!isExtraSpecRatioEnabled(options)) return FREE_EXTRA_RESOURCE_STEP

  return {
    step: ramGB > 0 ? ramGB : EXTRA_RESOURCE_STEP,
    decreaseStep: ramGB > EXTRA_RESOURCE_STEP ? ramGB / 2 : EXTRA_RESOURCE_STEP,
  }
}

export function clampExtraCpuCores(
  machine?: Machine,
  cpuCores = 0,
  options?: ExtraSpecPolicyOptions,
) {
  if (!machine || cpuCores <= 0) return 0
  if (!isExtraSpecRatioEnabled(options)) {
    return clampIntegerValue(cpuCores, getAvailableExtraCpuCores(machine))
  }

  return clampToOptions(cpuCores, getExtraCpuCoreOptions(machine))
}

export function clampExtraRamGB(
  machine?: Machine,
  ramGB = 0,
  extraCpuCores = 0,
  options?: ExtraSpecPolicyOptions,
) {
  if (!machine || ramGB <= 0) return 0
  if (!isExtraSpecRatioEnabled(options)) {
    return clampIntegerValue(ramGB, getAvailableExtraRamGB(machine))
  }

  return clampToOptions(ramGB, getExtraRamGBOptionsByCpuRatio(machine, extraCpuCores))
}

export function clampExtraStorageGB(
  machine?: Machine,
  diskGB = 0,
  options?: ExtraSpecPolicyOptions,
) {
  const maxDiskGB = getAvailableExtraStorageGB(machine, options)
  if (!machine || diskGB <= 0 || maxDiskGB <= 0) return 0

  const snappedDiskGB = ceilToStep(Math.floor(diskGB), getExtraDiskStepGB(options))

  return Math.min(Math.max(snappedDiskGB, getExtraDiskMinGB(options)), maxDiskGB)
}

export function normalizeExtraResourceConfig(
  machine?: Machine,
  config: ExtraResourceConfig = EMPTY_EXTRA_RESOURCE_CONFIG,
  options?: ExtraSpecPolicyOptions,
): ExtraResourceConfig {
  const diskGB = clampExtraStorageGB(machine, config.diskGB ?? 0, options)

  if (!isExtraSpecRatioEnabled(options)) {
    // No ratio rule: CPU and RAM are independent integers capped by the machine's extra units.
    const cpuCores = clampExtraCpuCores(machine, config.cpuCores ?? 0, options)
    const ramGB = clampExtraRamGB(machine, config.ramGB ?? 0, 0, options)

    return {
      cpuCores: cpuCores > 0 ? cpuCores : undefined,
      ramGB: ramGB > 0 ? ramGB : undefined,
      diskGB: diskGB > 0 ? diskGB : undefined,
    }
  }

  const hasCpuOrRamConfig = (config.cpuCores ?? 0) > 0 || (config.ramGB ?? 0) > 0
  let cpuCores = 0
  let ramGB = 0

  if (hasCpuOrRamConfig) {
    const cpuOptions = getExtraCpuCoreOptions(machine)

    cpuCores = clampToOptions(config.cpuCores ?? 0, cpuOptions)

    if (cpuCores <= 0 && (config.ramGB ?? 0) > 0) {
      cpuCores =
        cpuOptions.find((option) =>
          getExtraRamGBOptionsByCpuRatio(machine, option).some(
            (ramOption) => ramOption >= (config.ramGB ?? 0),
          ),
        ) ?? 0
    }

    if (cpuCores > 0) {
      const ramOptions = getExtraRamGBOptionsByCpuRatio(machine, cpuCores)

      if (ramOptions.length > 0) {
        const requestedRamGB = config.ramGB && config.ramGB > 0 ? config.ramGB : ramOptions[0]
        ramGB = clampToOptions(requestedRamGB, ramOptions)
      } else {
        cpuCores = 0
      }
    }

    if (cpuCores <= 0 || ramGB <= 0) {
      cpuCores = 0
      ramGB = 0
    }
  }

  return {
    cpuCores: cpuCores > 0 ? cpuCores : undefined,
    ramGB: ramGB > 0 ? ramGB : undefined,
    diskGB: diskGB > 0 ? diskGB : undefined,
  }
}

export function reconcileAutoExtraResourceConfig(
  machine: Machine,
  currentConfig: ExtraResourceConfig,
  requiredConfig: ExtraResourceConfig,
  trackedAutoConfig: ExtraResourceConfig,
  preserveCurrentConfig = false,
  options?: ExtraSpecPolicyOptions,
) {
  const normalizedCurrent = normalizeExtraResourceConfig(machine, currentConfig, options)
  const normalizedRequired = normalizeExtraResourceConfig(machine, requiredConfig, options)
  const nextConfig: ExtraResourceConfig = { ...normalizedCurrent }
  const nextTrackedAutoConfig: ExtraResourceConfig = { ...trackedAutoConfig }
  const fields: (keyof ExtraResourceConfig)[] = ['cpuCores', 'ramGB', 'diskGB']

  fields.forEach((field) => {
    const currentValue = normalizedCurrent[field] ?? 0
    const requiredValue = normalizedRequired[field] ?? 0
    const trackedValue = trackedAutoConfig[field] ?? 0

    if (requiredValue > currentValue) {
      nextConfig[field] = requiredValue
      if (preserveCurrentConfig) {
        delete nextTrackedAutoConfig[field]
      } else {
        nextTrackedAutoConfig[field] = requiredValue
      }
      return
    }

    if (preserveCurrentConfig) {
      delete nextTrackedAutoConfig[field]
      return
    }

    if (trackedValue > 0 && currentValue === trackedValue) {
      if (requiredValue > 0) {
        nextConfig[field] = requiredValue
        nextTrackedAutoConfig[field] = requiredValue
      } else {
        delete nextConfig[field]
        delete nextTrackedAutoConfig[field]
      }
      return
    }

    if (requiredValue === 0 && currentValue === 0) {
      delete nextTrackedAutoConfig[field]
    }
  })

  return {
    config: normalizeExtraResourceConfig(machine, nextConfig, options),
    trackedAutoConfig: nextTrackedAutoConfig,
  }
}

export function hasExtraResourceConfig(config?: ExtraResourceConfig) {
  return !!config && !!((config.cpuCores ?? 0) || (config.ramGB ?? 0) || (config.diskGB ?? 0))
}

function buildExtraMachineClassTag(config: ExtraResourceConfig) {
  const cpuCores = config.cpuCores ?? 0
  const ramMB = (config.ramGB ?? 0) * 1024
  const diskGB = config.diskGB ?? 0

  return [
    cpuCores > 0 ? `CPU${cpuCores}` : undefined,
    ramMB > 0 ? `RAM${ramMB}MB` : undefined,
    diskGB > 0 ? `DISK${diskGB}GB` : undefined,
  ]
    .filter(Boolean)
    .join('_')
}

export function buildExtraResourceDesc(
  machine?: Machine,
  config?: ExtraResourceConfig,
  options?: ExtraSpecPolicyOptions,
) {
  const normalizedConfig = normalizeExtraResourceConfig(machine, config, options)
  const cpus = normalizedConfig.cpuCores ?? 0
  const ramMB = (normalizedConfig.ramGB ?? 0) * 1024
  const diskGB = normalizedConfig.diskGB ?? 0
  if (!machine || !hasExtraResourceConfig(normalizedConfig)) return undefined

  return JSON.stringify({
    cpus: cpus > 0 ? cpus : undefined,
    ramMB: ramMB > 0 ? ramMB : undefined,
    diskGB: diskGB > 0 ? diskGB : undefined,
    machineClassTag: buildExtraMachineClassTag(normalizedConfig),
    machineCategory: machine.machineCategory,
    unitQty: 1,
  })
}
